import type { UserAvatar } from './UserAvatar'

export enum UserRole {
  DSTORRES = 'dstorres',
  OWNER = 'owner',
  SUPERADMIN = 'superadmin',
  ADMIN = 'admin',
  USER = 'user',
}

export const RoleLevels: Record<UserRole, number> = {
  [UserRole.DSTORRES]: 999,
  [UserRole.OWNER]: 100,
  [UserRole.SUPERADMIN]: 80,
  [UserRole.ADMIN]: 50,
  [UserRole.USER]: 10,
}

export function hasRequiredRole(userRole: string, required: UserRole): boolean {
  const level = RoleLevels[userRole as UserRole] ?? RoleLevels[UserRole.USER]
  return level >= RoleLevels[required]
}

export function rolesAtOrBelow(userRole: string): UserRole[] {
  const level = RoleLevels[userRole as UserRole] ?? RoleLevels[UserRole.USER]
  return (Object.entries(RoleLevels) as [UserRole, number][])
    .filter(([, l]) => l <= level && l < RoleLevels[UserRole.DSTORRES])
    .sort((a, b) => b[1] - a[1])
    .map(([role]) => role)
}

export interface ScopedChannel {
  db_id: string
  name: string
  description: string
  phone_hash_id: string
}

export interface ScopedTemplate {
  db_id: string
  name: string
  language: string
  status: string
  category: string
}

export interface AuthUser {
  user_id: string
  tenant_id: string
  /**
   * Nome legível do tenant (`tenant_profiles.name` no back). `null` quando o
   * tenant não tem profile cadastrado — e AUSENTE em back anterior a 08e4957a,
   * daí `string | null` em vez de `?:`: todo consumo checa vazio, e tipar como
   * opcional só espalharia `undefined` pelo JSX.
   */
  tenant_name: string | null
  name: string
  username: string
  email: string | null
  /**
   * Avatar do próprio usuário (`PATCH /users/me`). `| null` e não `?:` pelo
   * mesmo motivo de `tenant_name` logo acima: back mais velho que a tela manda
   * o campo AUSENTE, e espalhar `undefined` pelo JSX é pior que exigir o check
   * de vazio — todo consumo já usa `?? null`.
   */
  avatar: UserAvatar | null
  role: string
  permissions: string[]
  sub_roles: string[]
  channels: ScopedChannel[]
  templates: ScopedTemplate[]
}

export interface LoginResponse {
  access_token: string
  token_type: string
  /** SEGUNDOS até o `exp` do access (≤ 600, faixa 25 C2). O front não decodifica JWT. */
  expires_in: number
  tenant_id: string
  user_id: string
  role: string
  permissions: string[]
  channel_ids: string[]
  auto_generated_password: boolean
}
